import type { LocaleCodeType } from "@partners/ui";

import { assets } from "@partners/translations";
import { LocaleCode } from "@partners/ui";
import i18n, { type ReadCallback } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import resourcesToBackend from "i18next-resources-to-backend";
import { initReactI18next } from "react-i18next";

import { logger } from "@/utils/logger";

import { replaceInvalidURLParamLocale } from "./replaceInvalidURLParamLocale";

export const supportedLocales: LocaleCodeType[] = [
  LocaleCode.en,
  LocaleCode.ar,
  LocaleCode.zh,
  LocaleCode["zh-TW"],
  LocaleCode.th,
  LocaleCode.id,
  LocaleCode.vi,
  LocaleCode["es-419"],
  LocaleCode["pt-BR"],
  LocaleCode.mn,
];

const reportI18nInitializationError = (error: unknown) => {
  logger.error(error as Error);
};

i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  .use(
    resourcesToBackend(
      async (language: string, _namespace: string, callback: ReadCallback) => {
        try {
          const loader = assets[language as keyof typeof assets];
          if (!loader) throw new Error(`Language ${language} not found`);

          const translationModule = await loader();
          callback(null, translationModule.default);
        } catch (e) {
          callback(e as Error, null);
        }
      },
    ),
  )
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init(
    {
      supportedLngs: supportedLocales,
      load: "currentOnly",
      detection: {
        order: ["querystring", "localStorage", "navigator"],
        caches: ["localStorage"],
      },
      debug: import.meta.env.MODE === "development" && import.meta.env.DEV,
      fallbackLng: "en",
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
      },
    },
    () => {
      i18n.on("loaded", () => {
        logger.info("i18n initialized");
        replaceInvalidURLParamLocale(i18n);
      });
    },
  )
  .catch(reportI18nInitializationError);
